if (!customElements.get('ts-product-customizer')) {
  class TsProductCustomizer extends HTMLElement {
    connectedCallback() {
      const params = new URLSearchParams(window.location.search);
      if (params.get('customize') !== '1') return;

      this.product = JSON.parse(this.querySelector('[data-product-json]').textContent);
      this.form = this.querySelector('form');
      this.variant = this.querySelector('[data-tsp-variant]');
      this.sizeSelect = this.querySelector('[data-tsp-size]');
      this.productColorSelect = this.querySelector('[data-tsp-product-color]');

      this.baseVariants = this.product.variants.filter((item) => item.option3 === 'None');
      if (!this.baseVariants.length) this.baseVariants = this.product.variants;

      this.canvasFlip = this.querySelector('[data-tsp-canvas-flip]');
      this.viewTabs = Array.from(this.querySelectorAll('[data-tsp-view]'));
      this.currentViewLabel = this.querySelector('[data-tsp-current-view]');

      this.canvases = {};
      this.productImages = {};
      this.printAreas = {};
      this.designImages = {};
      this.designTexts = {};
      this.placeholders = {};
      this.querySelectorAll('[data-tsp-canvas]').forEach((canvas) => {
        const view = canvas.dataset.tspCanvas;
        this.canvases[view] = canvas;
        this.productImages[view] = canvas.querySelector('[data-tsp-product-image]');
        this.printAreas[view] = canvas.querySelector('[data-tsp-print-area]');
        this.designImages[view] = canvas.querySelector('[data-tsp-design-image]');
        this.designTexts[view] = canvas.querySelector('[data-tsp-design-text]');
        this.placeholders[view] = canvas.querySelector('[data-tsp-placeholder]');
      });
      this.viewNames = Object.keys(this.canvases);
      this.currentView = this.viewNames[0] || 'front';

      this.fileInputs = {};
      this.uploadLabels = {};
      this.fileRows = {};
      this.fileNameEls = {};
      this.querySelectorAll('[data-tsp-file]').forEach((input) => {
        this.fileInputs[input.dataset.tspFileView] = input;
      });
      this.querySelectorAll('[data-tsp-upload-for]').forEach((label) => {
        this.uploadLabels[label.dataset.tspUploadFor] = label;
      });
      this.querySelectorAll('[data-tsp-file-row]').forEach((row) => {
        this.fileRows[row.dataset.tspFileRow] = row;
      });
      this.querySelectorAll('[data-tsp-file-name]').forEach((el) => {
        this.fileNameEls[el.dataset.tspFileName] = el;
      });

      this.propInputs = {};
      this.querySelectorAll('[data-tsp-prop]').forEach((input) => {
        this.propInputs[input.dataset.tspProp] = input;
      });

      this.previewFileInput = this.querySelector('[data-tsp-preview-file]');
      this.previewAttached = false;
      this.submitting = false;

      this.views = {};
      this.viewNames.forEach((view) => {
        this.views[view] = {
          text: '',
          color: '#111111',
          font: 'Arial, sans-serif',
          fontSize: 25,
          scale: 1,
          rotate: 0,
          x: 0,
          y: 0,
          fileName: ''
        };
      });

      this.text = this.querySelector('[data-tsp-text]');
      this.fontSelect = this.querySelector('[data-tsp-font]');
      this.colorInput = this.querySelector('[data-tsp-color]');
      this.fontSizeInput = this.querySelector('[data-control="tsp-font-size"]');
      this.scaleInput = this.querySelector('[data-control="tsp-scale"]');
      this.rotateInput = this.querySelector('[data-control="tsp-rotate"]');

      this.step = 0;
      this.objectUrls = {};
      this.drag = null;

      this.populateVariants();
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.canvasFlip.addEventListener('pointerdown', this.onPointerDown.bind(this));
      window.addEventListener('pointermove', this.onPointerMove.bind(this));
      window.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      this.classList.add('is-active');
      document.body.classList.add('tsp-open');
      this.syncSiteHeaderOffset = this.updateSiteHeaderOffset.bind(this);
      this.updateSiteHeaderOffset();
      window.addEventListener('resize', this.syncSiteHeaderOffset);
      requestAnimationFrame(this.syncSiteHeaderOffset);
      this.setView(this.currentView, { skipAnimation: true });
      this.setStep(0);
    }

    updateSiteHeaderOffset() {
      const visibleHeaderParts = Array.from(
        document.querySelectorAll('#announcement-bar, #page-header')
      );
      const offset = visibleHeaderParts.reduce((largestBottom, element) => {
        if (element.hidden || getComputedStyle(element).display === 'none') return largestBottom;
        const bounds = element.getBoundingClientRect();
        if (bounds.height <= 0 || bounds.bottom <= 0) return largestBottom;
        return Math.max(largestBottom, bounds.bottom);
      }, 0);

      this.style.setProperty('--tsp-site-header-offset', `${Math.max(0, Math.round(offset))}px`);
    }

    populateVariants() {
      this.baseVariants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;
        const label = variant.option3 ? `${variant.option1} / ${variant.option2}` : variant.title;
        option.textContent = `${label}${variant.available ? '' : ' (Sold out)'}`;
        option.disabled = !variant.available;
        if (variant.available && !this.variant.value) option.selected = true;
        this.variant.appendChild(option);
      });
      const sizes = [...new Set(this.baseVariants.map((variant) => variant.option1).filter(Boolean))];
      const colors = [...new Set(this.baseVariants.map((variant) => variant.option2).filter(Boolean))];
      sizes.forEach((size) => this.sizeSelect.add(new Option(size, size)));
      colors.forEach((color) => this.productColorSelect.add(new Option(color, color)));
      this.setSingleOptionState(this.sizeSelect);
      this.setSingleOptionState(this.productColorSelect);
      const firstAvailable = this.baseVariants.find((variant) => variant.available) || this.baseVariants[0];
      if (firstAvailable) {
        this.sizeSelect.value = firstAvailable.option1 || '';
        this.productColorSelect.value = firstAvailable.option2 || '';
        this.variant.value = String(firstAvailable.id);
      }
      this.refreshVariantOptions();
      this.updateVariant();
    }

    setSingleOptionState(select) {
      const isSingle = select.options.length <= 1;
      select.closest('.tsp__field')?.classList.toggle('is-single-option', isSingle);
      if (isSingle) {
        select.setAttribute('aria-disabled', 'true');
        select.tabIndex = -1;
      } else {
        select.removeAttribute('aria-disabled');
        select.removeAttribute('tabindex');
      }
    }

    refreshVariantOptions(changedSelect) {
      const selectedSize = this.sizeSelect.value;
      const selectedColor = this.productColorSelect.value;
      Array.from(this.sizeSelect.options).forEach((option) => {
        option.disabled = !this.baseVariants.some(
          (variant) => variant.available && variant.option1 === option.value && (!selectedColor || variant.option2 === selectedColor)
        );
      });
      Array.from(this.productColorSelect.options).forEach((option) => {
        option.disabled = !this.baseVariants.some(
          (variant) => variant.available && variant.option2 === option.value && (!selectedSize || variant.option1 === selectedSize)
        );
      });
      let match = this.baseVariants.find(
        (variant) => variant.option1 === this.sizeSelect.value && variant.option2 === this.productColorSelect.value
      );
      if (!match || !match.available) {
        match = this.baseVariants.find((variant) => {
          if (!variant.available) return false;
          return changedSelect === this.sizeSelect
            ? variant.option1 === this.sizeSelect.value
            : variant.option2 === this.productColorSelect.value;
        }) || this.baseVariants.find((variant) => variant.available);
        if (match) {
          this.sizeSelect.value = match.option1 || '';
          this.productColorSelect.value = match.option2 || '';
        }
      }
      if (match) this.variant.value = String(match.id);
    }

    onClick(event) {
      if (event.target.closest('[data-tsp-back-page]')) {
        window.location.href = this.dataset.backUrl;
        return;
      }
      const viewTab = event.target.closest('[data-tsp-view]');
      if (viewTab) {
        this.setView(viewTab.dataset.tspView);
        return;
      }
      const tool = event.target.closest('[data-tsp-tool]');
      if (tool) this.setTool(tool.dataset.tspTool);
      const removeBtn = event.target.closest('[data-tsp-remove-image]');
      if (removeBtn) this.removeImage(removeBtn.dataset.tspRemoveImage);
      if (event.target.closest('[data-tsp-reset]')) this.resetTransform();
      if (event.target.closest('[data-tsp-next]')) {
        if (this.step === 0 && !this.hasAnyDesign()) {
          this.querySelector('[data-tsp-design-error]').textContent = this.dataset.designRequired;
          return;
        }
        this.querySelector('[data-tsp-design-error]').textContent = '';
        this.setStep(Math.min(1, this.step + 1));
      }
      if (event.target.closest('[data-tsp-previous]')) this.setStep(Math.max(0, this.step - 1));
    }

    onInput(event) {
      const state = this.views[this.currentView];
      const map = {
        'tsp-scale': 'scale',
        'tsp-rotate': 'rotate',
        'tsp-font-size': 'fontSize'
      };
      const key = event.target.dataset.control && map[event.target.dataset.control];
      if (key) {
        state[key] = Number(event.target.value);
        if (key === 'scale' || key === 'rotate') this.applyTransform();
        if (key === 'fontSize') this.style.setProperty('--tsp-font-size', `${event.target.value}px`);
      }
      if (event.target === this.text) {
        state.text = event.target.value;
        if (this.designTexts[this.currentView]) this.designTexts[this.currentView].textContent = event.target.value;
        this.updatePlaceholder(this.currentView);
      }
      if (event.target === this.colorInput) {
        state.color = event.target.value;
        this.style.setProperty('--tsp-text-color', event.target.value);
      }
      this.sync();
    }

    onChange(event) {
      if (event.target === this.variant) this.updateVariant();
      if (event.target === this.sizeSelect || event.target === this.productColorSelect) {
        this.refreshVariantOptions(event.target);
        this.updateVariant();
      }
      if (event.target.matches('[data-tsp-file]')) this.previewFile(event.target);
      if (event.target === this.fontSelect) {
        this.views[this.currentView].font = event.target.value;
        this.style.setProperty('--tsp-font', event.target.value);
      }
      this.sync();
    }

    updateVariant() {
      const variant = this.baseVariants.find((item) => String(item.id) === this.variant.value);
      if (!variant) return;
      this.form.querySelector('[name="id"]').value = variant.id;
      const image = variant.featured_image && variant.featured_image.src;
      if (this.productImages.front) {
        this.productImages.front.removeAttribute('srcset');
        this.productImages.front.src = image || this.product.featured_image;
      }
      this.querySelector('[data-tsp-submit]').disabled = !variant.available;
      this.sync();
    }

    updateDisplayedPrice() {
      const usedViews = this.viewNames.filter((view) => this.hasDesign(view));
      const tier = Math.min(usedViews.length, 3);
      const tierLabels = { 1: 'Front', 2: 'Front + Back', 3: 'Front + Back + Sleeve' };
      const pricedVariant = this.resolveFinalVariant(tierLabels[tier]);
      const price = this.querySelector('[data-tsp-price]');

      if (price && pricedVariant) price.textContent = this.money(pricedVariant.price);
    }

    previewFile(input) {
      const view = input.dataset.tspFileView;
      const file = input.files[0];
      const error = this.querySelector('[data-tsp-file-error]');
      error.textContent = '';
      if (!file) return;
      if (!file.type.startsWith('image/') || file.size > Number(this.dataset.maxFileMb) * 1024 * 1024) {
        error.textContent = this.dataset.fileError;
        input.value = '';
        return;
      }
      if (this.objectUrls[view]) URL.revokeObjectURL(this.objectUrls[view]);
      this.objectUrls[view] = URL.createObjectURL(file);
      if (this.designImages[view]) {
        this.designImages[view].src = this.objectUrls[view];
        this.designImages[view].style.display = 'block';
      }
      this.views[view].fileName = file.name;
      if (this.fileNameEls[view]) this.fileNameEls[view].textContent = file.name;
      this.updateFileRowVisibility(view);
      this.updatePlaceholder(view);
      if (view === this.currentView) this.setTool('upload');
      this.sync();
    }

    removeImage(view) {
      if (this.objectUrls[view]) URL.revokeObjectURL(this.objectUrls[view]);
      this.objectUrls[view] = null;
      if (this.fileInputs[view]) this.fileInputs[view].value = '';
      if (this.views[view]) this.views[view].fileName = '';
      if (this.designImages[view]) {
        this.designImages[view].removeAttribute('src');
        this.designImages[view].style.display = 'none';
      }
      this.updateFileRowVisibility(view);
      this.updatePlaceholder(view);
      this.sync();
    }

    updateFileRowVisibility(view) {
      if (!this.fileRows[view]) return;
      this.fileRows[view].hidden = !(view === this.currentView && this.views[view] && this.views[view].fileName);
    }

    updatePlaceholder(view) {
      if (!this.placeholders[view]) return;
      this.placeholders[view].hidden = this.hasDesign(view);
    }

    setTool(name) {
      this.querySelectorAll('[data-tsp-tool]').forEach((button) => {
        const active = button.dataset.tspTool === name;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
      });
      this.querySelectorAll('[data-tsp-tool-panel]').forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.tspToolPanel === name);
      });
    }

    hasDesign(view) {
      const state = this.views[view];
      return Boolean(state && (state.fileName || state.text.trim()));
    }

    hasAnyDesign() {
      return this.viewNames.some((view) => this.hasDesign(view));
    }

    setView(name, opts = {}) {
      if (!this.canvases[name]) return;
      const changed = name !== this.currentView;
      this.currentView = name;

      this.viewTabs.forEach((tab) => {
        const active = tab.dataset.tspView === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
      });

      Object.keys(this.canvases).forEach((view) => {
        this.canvases[view].classList.toggle('is-active', view === name);
      });

      if (changed && !opts.skipAnimation && this.canvasFlip) {
        this.canvasFlip.classList.remove('is-flipping');
        void this.canvasFlip.offsetWidth;
        this.canvasFlip.classList.add('is-flipping');
      }

      Object.keys(this.uploadLabels).forEach((view) => {
        this.uploadLabels[view].hidden = view !== name;
      });
      this.viewNames.forEach((view) => {
        this.updateFileRowVisibility(view);
        this.updatePlaceholder(view);
      });

      if (this.currentViewLabel) {
        const tab = this.viewTabs.find((item) => item.dataset.tspView === name);
        this.currentViewLabel.textContent = (tab && tab.dataset.tspViewLabel) || name;
      }

      this.loadViewControls(name);
      this.sync();
    }

    loadViewControls(name) {
      const state = this.views[name];
      if (!state) return;
      if (this.text) this.text.value = state.text;
      if (this.fontSelect) this.fontSelect.value = state.font;
      if (this.colorInput) this.colorInput.value = state.color;
      if (this.fontSizeInput) this.fontSizeInput.value = state.fontSize;
      if (this.scaleInput) this.scaleInput.value = state.scale;
      if (this.rotateInput) this.rotateInput.value = state.rotate;
      this.style.setProperty('--tsp-text-color', state.color);
      this.style.setProperty('--tsp-font', state.font);
      this.style.setProperty('--tsp-font-size', `${state.fontSize}px`);
      this.applyTransform();
    }

    onPointerDown(event) {
      const printArea = event.target.closest('.tsp__print-area');
      if (!printArea) return;
      const view = printArea.dataset.tspPrintArea;
      if (view !== this.currentView || !this.hasDesign(view)) return;
      event.preventDefault();
      printArea.setPointerCapture?.(event.pointerId);
      const state = this.views[view];
      this.drag = {
        pointerId: event.pointerId,
        view,
        printArea,
        startX: event.clientX,
        startY: event.clientY,
        originX: state.x,
        originY: state.y
      };
    }

    onPointerMove(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      const bounds = this.drag.printArea.getBoundingClientRect();
      const limitX = bounds.width * 0.42;
      const limitY = bounds.height * 0.42;
      const state = this.views[this.drag.view];
      state.x = Math.max(-limitX, Math.min(limitX, this.drag.originX + event.clientX - this.drag.startX));
      state.y = Math.max(-limitY, Math.min(limitY, this.drag.originY + event.clientY - this.drag.startY));
      this.applyTransform();
      this.sync();
    }

    onPointerUp(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.drag = null;
    }

    applyTransform() {
      const state = this.views[this.currentView];
      if (!state) return;
      this.style.setProperty('--tsp-x', `${state.x}px`);
      this.style.setProperty('--tsp-y', `${state.y}px`);
      this.style.setProperty('--tsp-scale', state.scale);
      this.style.setProperty('--tsp-rotate', `${state.rotate}deg`);
    }

    resetTransform() {
      const state = this.views[this.currentView];
      if (!state) return;
      state.scale = 1;
      state.rotate = 0;
      state.x = 0;
      state.y = 0;
      if (this.scaleInput) this.scaleInput.value = 1;
      if (this.rotateInput) this.rotateInput.value = 0;
      this.applyTransform();
      this.sync();
    }

    setStep(index) {
      this.step = index;
      this.querySelectorAll('[data-tsp-step]').forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === index);
      });
      this.querySelectorAll('[data-tsp-progress]').forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex <= index);
      });
      this.sync();
    }

    sync() {
      this.viewNames.forEach((view) => {
        const state = this.views[view];
        const setProp = (field, value) => {
          const input = this.propInputs[`${view}:${field}`];
          if (input) input.value = value;
        };
        setProp('text', state.text.trim());
        setProp('color', state.color);
        setProp('font', state.font);
        setProp('scale', `${Math.round(state.scale * 100)}%`);
        setProp('position', `X ${Math.round(state.x)}, Y ${Math.round(state.y)}`);
        setProp('rotation', `${Math.round(state.rotate)}°`);
      });

      this.querySelector('[data-tsp-summary-variant]').textContent =
        this.variant.options[this.variant.selectedIndex]?.textContent || '';

      const sideSummaries = this.viewNames
        .filter((view) => this.hasDesign(view))
        .map((view) => {
          const tab = this.viewTabs.find((item) => item.dataset.tspView === view);
          const label = (tab && tab.dataset.tspViewLabel) || view;
          const state = this.views[view];
          const parts = [state.fileName, state.text.trim()].filter(Boolean).join(' + ');
          return `${label}: ${parts}`;
        });
      this.querySelector('[data-tsp-summary-design]').textContent =
        sideSummaries.join(' | ') || this.dataset.noDesign;

      this.updateDisplayedPrice();
    }

    onSubmit(event) {
      event.preventDefault();
      if (!this.hasAnyDesign()) {
        this.querySelector('[data-tsp-design-error]').textContent = this.dataset.designRequired;
        this.setStep(0);
        return;
      }
      if (this.submitting) return;
      this.submitting = true;
      this.setCartError('');
      const submitButton = this.querySelector('[data-tsp-submit]');
      this.submitButtonLabel = submitButton.textContent;
      submitButton.textContent = this.dataset.addingLabel;
      submitButton.disabled = true;
      if (this.previewFileInput && !this.previewAttached) {
        this.attachPreviewAndSubmit();
      } else {
        this.submitOrder();
      }
    }

    buildPreviewCanvas() {
      const view = this.viewNames.find((name) => this.hasDesign(name));
      if (!view) return null;
      const productImg = this.productImages[view];
      const printArea = this.printAreas[view];
      if (!productImg || !printArea || !productImg.naturalWidth) return null;

      const canvas = document.createElement('canvas');
      canvas.width = productImg.naturalWidth;
      canvas.height = productImg.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(productImg, 0, 0, canvas.width, canvas.height);

      const pTop = (parseFloat(printArea.style.getPropertyValue('--tsp-top')) || 0) / 100;
      const pLeft = (parseFloat(printArea.style.getPropertyValue('--tsp-left')) || 0) / 100;
      const pWidth = (parseFloat(printArea.style.getPropertyValue('--tsp-width')) || 0) / 100;
      const pHeight = (parseFloat(printArea.style.getPropertyValue('--tsp-height')) || 0) / 100;

      const areaX = pLeft * canvas.width;
      const areaY = pTop * canvas.height;
      const areaW = pWidth * canvas.width;
      const areaH = pHeight * canvas.height;
      const centerX = areaX + areaW / 2;
      const centerY = areaY + areaH / 2;

      const state = this.views[view];

      const wasActive = this.canvases[view].classList.contains('is-active');
      if (!wasActive) this.canvases[view].classList.add('is-active');
      const onScreenRect = printArea.getBoundingClientRect();
      if (!wasActive) this.canvases[view].classList.remove('is-active');

      const ratio = onScreenRect.width > 0 ? areaW / onScreenRect.width : 1;
      const layerCx = centerX + (state.x || 0) * ratio;
      const layerCy = centerY + (state.y || 0) * ratio;

      ctx.save();
      ctx.translate(layerCx, layerCy);
      ctx.rotate(((state.rotate || 0) * Math.PI) / 180);
      ctx.scale(state.scale || 1, state.scale || 1);

      const designImg = this.designImages[view];
      if (state.fileName && designImg && designImg.naturalWidth) {
        const boxW = areaW * 0.92 * 0.92;
        const boxH = areaH * 0.92 * 0.92;
        const imgRatio = designImg.naturalWidth / designImg.naturalHeight;
        let drawW = boxW;
        let drawH = boxW / imgRatio;
        if (drawH > boxH) {
          drawH = boxH;
          drawW = boxH * imgRatio;
        }
        ctx.drawImage(designImg, -drawW / 2, -drawH / 2, drawW, drawH);
      } else if (state.text && state.text.trim()) {
        const fontSize = (state.fontSize || 25) * ratio;
        ctx.font = `700 ${fontSize}px ${state.font || 'Arial, sans-serif'}`;
        ctx.fillStyle = state.color || '#111111';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const lines = state.text.split('\n');
        const lineHeight = fontSize * 1.1;
        const startY = -((lines.length - 1) * lineHeight) / 2;
        lines.forEach((line, index) => {
          ctx.fillText(line, 0, startY + index * lineHeight);
        });
      }
      ctx.restore();

      return canvas;
    }

    attachPreviewAndSubmit() {
      const finish = () => {
        this.previewAttached = true;
        this.submitOrder();
      };
      let canvas = null;
      try {
        canvas = this.buildPreviewCanvas();
      } catch (err) {
        canvas = null;
      }
      if (!canvas) {
        finish();
        return;
      }
      try {
        canvas.toBlob((blob) => {
          try {
            if (blob) {
              const file = new File([blob], 'preview.png', { type: 'image/png' });
              const dataTransfer = new DataTransfer();
              dataTransfer.items.add(file);
              this.previewFileInput.files = dataTransfer.files;
            }
          } catch (err) {
            // fall back silently to the default product image
          }
          finish();
        }, 'image/png', 0.92);
      } catch (err) {
        finish();
      }
    }

    resolveFinalVariant(tierLabel) {
      const base = this.baseVariants.find((item) => String(item.id) === this.variant.value);
      if (!base) return null;
      if (!tierLabel || !base.option3) return base;
      const match = this.product.variants.find(
        (item) => item.option1 === base.option1 && item.option2 === base.option2 && item.option3 === tierLabel
      );
      return match || base;
    }

    submitOrder() {
      const usedViews = this.viewNames.filter((view) => this.hasDesign(view));
      const tier = Math.min(usedViews.length, 3);
      const tierLabels = { 1: 'Front', 2: 'Front + Back', 3: 'Front + Back + Sleeve' };
      const finalVariant = this.resolveFinalVariant(tierLabels[tier]);
      if (finalVariant) {
        this.form.querySelector('[name="id"]').value = finalVariant.id;
      }
      const sidesInput = this.form.querySelector('[name="properties[_Customized sides]"]');
      if (sidesInput) {
        sidesInput.value = usedViews
          .map((view) => {
            const tab = this.viewTabs.find((item) => item.dataset.tspView === view);
            return (tab && tab.dataset.tspViewLabel) || view;
          })
          .join(' + ');
      }

      const formData = new FormData(this.form);
      fetch(this.form.action, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' }
      })
        .then((response) =>
          response.json().then((data) => {
            if (!response.ok) throw new Error((data && data.description) || 'add_failed');
            return data;
          })
        )
        .then(() => {
          window.location.href = '/cart';
        })
        .catch(() => {
          this.setCartError(this.dataset.cartError || 'Something went wrong adding this to your cart. Please try again.');
          this.resetSubmitButton();
        });
    }

    setCartError(message) {
      const el = this.querySelector('[data-tsp-cart-error]');
      if (el) el.textContent = message || '';
    }

    resetSubmitButton() {
      this.submitting = false;
      const submitButton = this.querySelector('[data-tsp-submit]');
      submitButton.disabled = false;
      submitButton.textContent = this.submitButtonLabel || submitButton.textContent;
    }

    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: this.dataset.currency || 'INR'
      }).format(Number(cents || 0) / 100);
    }
  }

  customElements.define('ts-product-customizer', TsProductCustomizer);
}
